import { useState } from 'react';
import { X, Upload, Trash2, Download, FileSpreadsheet, Sparkles, ChevronRight, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../../../styling/stylingHelper';
import FileDropzone from '../../../ui/FileDropzone';
import { getMidLevelCategoryOptionGroups } from '../../../orderhistory/categoryUtils';
import { downloadCustomIngredientTemplate } from '../customIngredientTemplateExcel';
import {
  ExtractedPriceRow,
  TemplateIngredientRow,
  BulkUpsertResult,
  extractSupplierPriceFile,
  parseCustomIngredientTemplate,
  bulkCreateCustomIngredients,
} from '../../../../services/customIngredients/customIngredientsService';

interface UploadSupplierPriceFileModalProps {
  restaurantId: string;
  onClose: () => void;
  onIngredientsCreated: () => void;
}

const UNIT_OPTIONS = ['g', 'ml', 'piece'];

// 'priceFile' = freeform supplier document read by AI; 'template' = our Excel template.
type ImportMode = 'priceFile' | 'template';

// One row in the review table. Template rows carry their own supplier / VAT / category;
// price-file rows get those from the modal-level supplier field + VAT toggle on save.
type ReviewRow = ExtractedPriceRow & Partial<Omit<TemplateIngredientRow, keyof ExtractedPriceRow>>;

// A row whose quantity could not be derived (defaulted to 1 piece, no hint) needs review.
const needsReview = (row: ExtractedPriceRow): boolean =>
  !row.weightString && row.quantityInfo.stdUnit === 'piece' && row.quantityInfo.stdAmount === 1;

// Whether a template row still blocks saving: either the parser complained about it
// (cleared as soon as the user edits that row) or a required value is missing.
const templateRowIssues = (row: ReviewRow): boolean =>
  (row.errors?.length ?? 0) > 0 ||
  !row.supplierName?.trim() ||
  !row.productName.trim() ||
  !(row.price > 0) ||
  !(row.quantityInfo.stdAmount > 0);

const UploadSupplierPriceFileModal = ({ restaurantId, onClose, onIngredientsCreated }: UploadSupplierPriceFileModalProps) => {
  const { t } = useTranslation();

  const [mode, setMode] = useState<ImportMode | null>(null);
  const [step, setStep] = useState<'source' | 'upload' | 'review' | 'result'>('source');
  const [supplierName, setSupplierName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<ReviewRow[]>([]);
  // Set when the template held more rows than the parser returns, so we can say so.
  const [droppedRowCount, setDroppedRowCount] = useState(0);
  const [result, setResult] = useState<BulkUpsertResult | null>(null);
  // One file-wide VAT rate applied to every extracted (ex-VAT) row. Default 9%.
  // Price-file mode only — template rows carry their own rate.
  const [vatRate, setVatRate] = useState<'9' | '21'>('9');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rateDecimal = vatRate === '21' ? 0.21 : 0.09;
  const isTemplate = mode === 'template';
  const invalidRowCount = isTemplate ? rows.filter(templateRowIssues).length : 0;

  // Switching source starts clean — the two flows accept different file types, so a
  // file picked for the other one must not carry over.
  const chooseMode = (next: ImportMode) => {
    setMode(next);
    setStep('upload');
    setError(null);
    setFiles([]);
    setRows([]);
    setResult(null);
    setDroppedRowCount(0);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadCustomIngredientTemplate();
    } catch (err) {
      console.error('Failed to build custom ingredient template:', err);
      setError(t('costPrice.importTemplate.downloadFailed'));
    }
  };

  const handleExtract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supplierName.trim()) {
      setError(t('costPrice.uploadPriceFile.supplierRequired'));
      return;
    }
    if (files.length === 0) {
      setError(t('costPrice.uploadPriceFile.filesRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const { rows: extracted } = await extractSupplierPriceFile(files);
      if (extracted.length === 0) {
        setError(t('costPrice.uploadPriceFile.noRowsExtracted'));
        return;
      }
      setRows(extracted);
      setStep('review');
    } catch (err) {
      console.error('Failed to extract supplier price file:', err);
      setError(t('costPrice.uploadPriceFile.extractFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleParseTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) {
      setError(t('costPrice.importTemplate.fileRequired'));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const { rows: parsed, totalRows } = await parseCustomIngredientTemplate(files[0]);
      if (parsed.length === 0) {
        setError(t('costPrice.importTemplate.noRows'));
        return;
      }
      setRows(parsed);
      setDroppedRowCount(Math.max(0, totalRows - parsed.length));
      setStep('review');
    } catch (err) {
      console.error('Failed to parse custom ingredient template:', err);
      setError(err instanceof Error ? err.message : t('costPrice.importTemplate.parseFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Editing a row also clears the parser's complaints about it — the user is fixing it.
  const updateRow = (index: number, patch: Partial<ReviewRow>) => {
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, ...patch, errors: [] } : r)));
  };

  const updateQuantity = (index: number, patch: Partial<ExtractedPriceRow['quantityInfo']>) => {
    setRows(prev =>
      prev.map((r, i) => (i === index ? { ...r, quantityInfo: { ...r.quantityInfo, ...patch }, errors: [] } : r))
    );
  };

  const removeRow = (index: number) => {
    setRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (rows.length === 0) {
      setError(t('costPrice.uploadPriceFile.noRowsExtracted'));
      return;
    }
    if (isTemplate && invalidRowCount > 0) {
      setError(t('costPrice.importTemplate.fixRowsFirst', { count: invalidRowCount }));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      if (isTemplate) {
        // Each row brings its own supplier + VAT + category; existing ingredients are skipped.
        const bulkRows = rows.map(row => ({
          ...row,
          supplierName: row.supplierName?.trim(),
          taxRate: row.taxRate ?? 0.09,
          priceWithTaxes: Math.round(row.price * (1 + (row.taxRate ?? 0.09)) * 10000) / 10000,
        }));
        const saved = await bulkCreateCustomIngredients(restaurantId, '', bulkRows, true);
        setResult(saved);
        setStep('result');
        onIngredientsCreated();
        return;
      }

      // Apply the file-wide rate to each ex-VAT row to derive incl-VAT price + rate.
      const bulkRows = rows.map(row => ({
        ...row,
        priceWithTaxes: Math.round(row.price * (1 + rateDecimal) * 10000) / 10000,
        taxRate: rateDecimal,
      }));
      await bulkCreateCustomIngredients(restaurantId, supplierName.trim(), bulkRows);
      onIngredientsCreated();
      onClose();
    } catch (err) {
      console.error('Failed to save custom ingredients:', err);
      setError(t('costPrice.uploadPriceFile.saveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = cn(
    'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100',
    borderPrimary,
    whiteBG,
    primaryText,
    smallTextSize
  );

  const secondaryButtonClass = cn(
    smallTextSize, secondaryText, semiBoldText, 'rounded-xl px-4 py-3', whiteBG, 'border', borderPrimary,
    'hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark', 'disabled:opacity-50'
  );

  const primaryButtonClass = cn(
    smallTextSize, 'text-white dark:text-white', semiBoldText,
    'bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-6 py-3', 'hover:bg-darkGreenColor/95',
    'flex items-center justify-center gap-2', 'disabled:opacity-50 disabled:cursor-not-allowed'
  );

  const renderSourceOption = (
    value: ImportMode,
    icon: React.ReactNode,
    title: string,
    description: string
  ) => (
    <button
      type="button"
      onClick={() => chooseMode(value)}
      className={cn(
        'w-full text-left p-4 rounded-xl border flex items-start gap-3',
        borderPrimary, whiteBG,
        'hover:border-darkGreenColor dark:hover:border-darkGreenColor-dark hover:bg-[#F1FAF7] dark:hover:bg-darkGreenColor/10'
      )}
    >
      <span className="text-darkGreenColor dark:text-darkGreenColor-dark mt-0.5">{icon}</span>
      <span className="flex-1">
        <span className={cn(semiBoldText, baseTextSize, primaryText, 'block')}>{title}</span>
        <span className={cn(smallTextSize, secondaryText, 'block mt-1')}>{description}</span>
      </span>
      <ChevronRight className={cn('w-4 h-4 mt-1', secondaryText)} />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/60 flex items-center justify-center z-[60] p-0 sm:p-4">
      <div className={cn(
        'bg-white dark:bg-background-dark rounded-none sm:rounded-xl shadow-xl w-full border flex flex-col h-full sm:h-auto max-h-none sm:max-h-[90vh]',
        step === 'review' ? (isTemplate ? 'max-w-6xl' : 'max-w-4xl') : 'max-w-md',
        borderPrimary
      )}>
        <div className="p-4 border-b border-borderPrimary dark:border-borderPrimary-dark flex justify-between items-start">
          <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
            {isTemplate ? t('costPrice.importTemplate.title') : t('costPrice.uploadPriceFile.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200">
              {error}
            </div>
          )}

          {step === 'source' && (
            <div className="space-y-3">
              <p className={cn(smallTextSize, secondaryText)}>{t('costPrice.importSource.hint')}</p>
              {renderSourceOption(
                'priceFile',
                <Sparkles className="w-5 h-5" />,
                t('costPrice.importSource.priceFileTitle'),
                t('costPrice.importSource.priceFileDescription')
              )}
              {renderSourceOption(
                'template',
                <FileSpreadsheet className="w-5 h-5" />,
                t('costPrice.importSource.templateTitle'),
                t('costPrice.importSource.templateDescription')
              )}
            </div>
          )}

          {step === 'upload' && !isTemplate && (
            <form onSubmit={handleExtract} className="space-y-4">
              <div>
                <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                  {t('costPrice.uploadPriceFile.supplierName')} *
                </label>
                <input
                  type="text"
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  placeholder={t('costPrice.uploadPriceFile.supplierNamePlaceholder')}
                  className={cn(
                    'w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100',
                    borderPrimary, whiteBG, primaryText
                  )}
                  required
                />
              </div>

              <div>
                <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                  {t('costPrice.uploadPriceFile.selectFiles')} *
                </label>
                <FileDropzone
                  files={files}
                  onFilesChange={setFiles}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*"
                  multiple
                  disabled={isLoading}
                  hint={t('costPrice.uploadPriceFile.selectFilesHint')}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setStep('source'); setMode(null); setError(null); }}
                  disabled={isLoading}
                  className={cn(secondaryButtonClass, 'flex-1')}
                >
                  {t('costPrice.uploadPriceFile.back')}
                </button>
                <button type="submit" disabled={isLoading} className={cn(primaryButtonClass, 'flex-1 px-4 py-3')}>
                  <Upload className="w-4 h-4" />
                  {isLoading ? t('costPrice.uploadPriceFile.extracting') : t('costPrice.uploadPriceFile.extract')}
                </button>
              </div>
            </form>
          )}

          {step === 'upload' && isTemplate && (
            <form onSubmit={handleParseTemplate} className="space-y-4">
              <div className={cn('p-3 rounded-xl border', borderPrimary)}>
                <p className={cn(smallTextSize, secondaryText, 'mb-3')}>{t('costPrice.importTemplate.downloadHint')}</p>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className={cn(secondaryButtonClass, 'w-full flex items-center justify-center gap-2')}
                >
                  <Download className="w-4 h-4" />
                  {t('costPrice.importTemplate.downloadButton')}
                </button>
              </div>

              <div>
                <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                  {t('costPrice.importTemplate.selectFile')} *
                </label>
                <FileDropzone
                  files={files}
                  onFilesChange={setFiles}
                  accept=".xlsx,.xls,.csv"
                  disabled={isLoading}
                  hint={t('costPrice.importTemplate.selectFileHint')}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setStep('source'); setMode(null); setError(null); }}
                  disabled={isLoading}
                  className={cn(secondaryButtonClass, 'flex-1')}
                >
                  {t('costPrice.uploadPriceFile.back')}
                </button>
                <button type="submit" disabled={isLoading} className={cn(primaryButtonClass, 'flex-1 px-4 py-3')}>
                  <Upload className="w-4 h-4" />
                  {isLoading ? t('costPrice.importTemplate.reading') : t('costPrice.importTemplate.readFile')}
                </button>
              </div>
            </form>
          )}

          {step === 'review' && (
            <div>
              <p className={cn(smallTextSize, secondaryText, 'mb-3')}>
                {isTemplate
                  ? t('costPrice.importTemplate.reviewHint', { count: rows.length })
                  : t('costPrice.uploadPriceFile.reviewHint', { count: rows.length, supplier: supplierName.trim() })}
              </p>

              {isTemplate && droppedRowCount > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
                  {t('costPrice.importTemplate.truncated', { count: droppedRowCount })}
                </div>
              )}

              {isTemplate && invalidRowCount > 0 && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('costPrice.importTemplate.fixRowsFirst', { count: invalidRowCount })}</span>
                </div>
              )}

              {/* File-wide VAT rate — applied to every row's ex-VAT price on save.
                  Template rows carry their own rate, so this is price-file only. */}
              {!isTemplate && (
                <div className="mb-4">
                  <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                    {t('costPrice.uploadPriceFile.vatRateLabel')} *
                  </label>
                  <div className="flex gap-3 max-w-xs">
                    {(['9', '21'] as const).map(rate => (
                      <button
                        key={rate}
                        type="button"
                        onClick={() => setVatRate(rate)}
                        disabled={isLoading}
                        className={cn(
                          'flex-1 px-4 py-2 rounded-xl border transition-colors',
                          vatRate === rate
                            ? 'border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-cardLightGreen-dark text-darkGreenColor dark:text-darkGreenColor-dark font-semibold'
                            : cn(borderPrimary, 'bg-white hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark', secondaryText),
                          'disabled:opacity-50'
                        )}
                      >
                        {rate}%
                      </button>
                    ))}
                  </div>
                  <p className={cn(smallTextSize, secondaryText, 'mt-1')}>
                    {t('costPrice.vatRate.helper')}
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={cn(smallTextSize, secondaryText, mediumText, 'text-left')}>
                      {isTemplate && <th className="px-2 py-2 w-40">{t('costPrice.importTemplate.colSupplier')}</th>}
                      <th className="px-2 py-2">{t('costPrice.uploadPriceFile.colProduct')}</th>
                      <th className="px-2 py-2 w-28">{t('costPrice.uploadPriceFile.colArticleId')}</th>
                      <th className="px-2 py-2 w-24">{t('costPrice.uploadPriceFile.colPrice')}</th>
                      <th className="px-2 py-2 w-24">{t('costPrice.uploadPriceFile.colAmount')}</th>
                      <th className="px-2 py-2 w-24">{t('costPrice.uploadPriceFile.colUnit')}</th>
                      {isTemplate && <th className="px-2 py-2 w-20">{t('costPrice.importTemplate.colVat')}</th>}
                      {isTemplate && <th className="px-2 py-2 w-44">{t('costPrice.importTemplate.colCategory')}</th>}
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const rowInvalid = isTemplate && templateRowIssues(row);
                      return (
                        <tr
                          key={index}
                          className={cn(
                            'border-t border-borderPrimary dark:border-borderPrimary-dark align-top',
                            rowInvalid && 'bg-amber-50/60 dark:bg-amber-950/20'
                          )}
                        >
                          {isTemplate && (
                            <td className="px-2 py-2">
                              <input
                                type="text"
                                value={row.supplierName ?? ''}
                                onChange={(e) => updateRow(index, { supplierName: e.target.value })}
                                className={inputClass}
                              />
                              {row.rowNumber !== undefined && (
                                <span className={cn(smallTextSize, secondaryText)}>
                                  {t('costPrice.importTemplate.sheetRow', { row: row.rowNumber })}
                                </span>
                              )}
                            </td>
                          )}
                          <td className="px-2 py-2">
                            <input
                              type="text"
                              value={row.productName}
                              onChange={(e) => updateRow(index, { productName: e.target.value })}
                              className={inputClass}
                            />
                            {!isTemplate && needsReview(row) && (
                              <span className={cn(smallTextSize, 'text-amber-600 dark:text-amber-400')}>
                                {t('costPrice.uploadPriceFile.needsReview')}
                              </span>
                            )}
                            {isTemplate && (row.errors?.length ?? 0) > 0 && (
                              <span className={cn(smallTextSize, 'text-amber-600 dark:text-amber-400 block')}>
                                {row.errors!.join(' · ')}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="text"
                              value={row.articleId ?? ''}
                              onChange={(e) => updateRow(index, { articleId: e.target.value })}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={row.price}
                              onChange={(e) => updateRow(index, { price: parseFloat(e.target.value) || 0 })}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={row.quantityInfo.stdAmount}
                              onChange={(e) => updateQuantity(index, { stdAmount: parseFloat(e.target.value) || 0 })}
                              className={inputClass}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              value={row.quantityInfo.stdUnit}
                              onChange={(e) => updateQuantity(index, { stdUnit: e.target.value })}
                              className={inputClass}
                            >
                              {UNIT_OPTIONS.map(u => (
                                <option key={u} value={u}>{u === 'piece' ? t('costPrice.addIngredient.piece') : u}</option>
                              ))}
                            </select>
                          </td>
                          {isTemplate && (
                            <td className="px-2 py-2">
                              <select
                                value={row.taxRate === 0.21 ? '21' : '9'}
                                onChange={(e) => updateRow(index, { taxRate: e.target.value === '21' ? 0.21 : 0.09 })}
                                className={inputClass}
                              >
                                <option value="9">9%</option>
                                <option value="21">21%</option>
                              </select>
                            </td>
                          )}
                          {isTemplate && (
                            <td className="px-2 py-2">
                              <select
                                value={row.category ?? ''}
                                onChange={(e) => updateRow(index, { category: e.target.value || undefined })}
                                className={inputClass}
                              >
                                <option value="">{t('costPrice.createCustomIngredient.noCategory')}</option>
                                {getMidLevelCategoryOptionGroups().map(group => (
                                  <optgroup key={group.label} label={group.label}>
                                    {group.options.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </td>
                          )}
                          <td className="px-2 py-2">
                            <button
                              onClick={() => removeRow(index)}
                              className="text-[#D92D20] dark:text-textRed-dark hover:bg-[#FEF3F2] dark:hover:bg-red-500/10 rounded-lg p-2"
                              aria-label={t('costPrice.uploadPriceFile.remove')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'result' && result && (
            <div className="space-y-3">
              <p className={cn(baseTextSize, primaryText, semiBoldText)}>
                {t('costPrice.importTemplate.createdCount', { count: result.created })}
              </p>
              {result.skippedExisting.length > 0 && (
                <div className={cn(smallTextSize, secondaryText)}>
                  <p className={cn(mediumText, 'mb-1')}>
                    {t('costPrice.importTemplate.skippedCount', { count: result.skippedExisting.length })}
                  </p>
                  <p>{result.skippedExisting.slice(0, 20).join(', ')}{result.skippedExisting.length > 20 ? '…' : ''}</p>
                </div>
              )}
              {result.duplicatesMerged > 0 && (
                <p className={cn(smallTextSize, secondaryText)}>
                  {t('costPrice.importTemplate.duplicatesInFile', { count: result.duplicatesMerged })}
                </p>
              )}
            </div>
          )}
        </div>

        {step === 'review' && (
          <div className="p-4 border-t border-borderPrimary dark:border-borderPrimary-dark flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { setStep('upload'); setError(null); }}
              disabled={isLoading}
              className={secondaryButtonClass}
            >
              {t('costPrice.uploadPriceFile.back')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isLoading || rows.length === 0 || invalidRowCount > 0}
              className={primaryButtonClass}
            >
              {isLoading ? t('costPrice.uploadPriceFile.saving') : t('costPrice.uploadPriceFile.saveAll', { count: rows.length })}
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="p-4 border-t border-borderPrimary dark:border-borderPrimary-dark flex justify-end">
            <button type="button" onClick={onClose} className={primaryButtonClass}>
              {t('costPrice.uploadSubrecipeTemplate.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadSupplierPriceFileModal;
